import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatCampaignDate } from './campaignDashboardUtils';
import { smsCounts, smsRecipientCount } from './campaignDashboardUtils';

function buildExcelSheetName(baseName, dateValue) {
  let formattedDate = '';
  if (dateValue) {
    try {
      const date = new Date(dateValue);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      formattedDate = `_${day}.${month}.${year}-${hours}.${minutes}`;
    } catch {
      formattedDate = `_${Date.now()}`;
    }
  }
  const availableLengthForName =
    31 - formattedDate.length - (formattedDate ? 1 : 0);
  const truncatedName =
    baseName.length > availableLengthForName
      ? baseName.slice(0, availableLengthForName)
      : baseName;
  return `${truncatedName}${formattedDate}`.slice(0, 31);
}

export async function exportEmailCampaignsToXlsx(campaigns) {
  const workbook = XLSX.utils.book_new();

  for (const campaign of campaigns) {
    const summaryData = [
      {
        'Campaign Name': campaign.campaignName,
        'Date Sent': campaign.sentDate,
        Recipients: campaign.recipients || 0,
        Opens: campaign.opens || 0,
        Clicks: campaign.clicks || 0,
        Unsubscribed: campaign.unsubscribed || 0,
        Status: campaign.status === 'draft' ? 'draft' : campaign.status || 'pending',
      },
    ];

    let emailDetails = [];
    try {
      const response = await axios.get(`/api/campaigns/${campaign._id}/contacts`);
      emailDetails = response.data;
    } catch (error) {
      console.error('Error fetching contacts for campaign:', campaign._id, error);
    }

    const emailData = emailDetails.map((item) => ({
      Contact: item.email,
      Status: item.status,
      'Date Sent': item.dateSent,
      'Date Viewed': item.dateViewed,
    }));

    const sheetData = [
      ...summaryData,
      {},
      {
        Contact: 'Contact',
        Status: 'Status',
        'Date Sent': 'Date Sent',
        'Date Viewed': 'Date Viewed',
      },
      ...emailData,
    ];

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { skipHeader: true });
    const sheetName = buildExcelSheetName(
      campaign.campaignName || 'Campaign',
      campaign.sentDate,
    );
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  }

  XLSX.writeFile(workbook, 'Campaigns.xlsx');
}

export function exportSmsCampaignsToXlsx(campaigns) {
  const workbook = XLSX.utils.book_new();

  for (const campaign of campaigns) {
    const total = smsRecipientCount(campaign);
    const counts = smsCounts(campaign);
    const summaryData = [
      {
        'Campaign Name': campaign.campaignName,
        'Date Created': formatCampaignDate(campaign.createdAt),
        Sender: campaign.senderPhone,
        Recipients: total,
        Sent: counts.sent,
        Delivered: counts.delivered,
        Failed: counts.failed,
        Segments: campaign.totalSegments ?? 0,
        'Credits charged': campaign.creditsCharged ?? 0,
        Status: campaign.status,
      },
    ];

    const recipientData = (campaign.recipientResults || []).map((row) => ({
      Phone: row.to,
      'Send status': row.sendStatus,
      'Delivery status': row.deliveryStatus || '',
      'Send error': row.sendError || '',
      'Delivery error': row.deliveryError || '',
    }));

    const sheetData = [
      ...summaryData,
      {},
      {
        Phone: 'Phone',
        'Send status': 'Send status',
        'Delivery status': 'Delivery status',
        'Send error': 'Send error',
        'Delivery error': 'Delivery error',
      },
      ...recipientData,
    ];

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { skipHeader: true });
    const sheetName = buildExcelSheetName(
      campaign.campaignName || 'SMS Campaign',
      campaign.createdAt,
    );
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  }

  XLSX.writeFile(workbook, 'SMS_Campaigns.xlsx');
}
